/**
 * Visit counting, only after the visitor accepts analytics cookies (the banner). Two cookies: the
 * choice (bl_consent: 13 months if accepted, 6 if refused, then we ask again) and, if accepted, a
 * random visitor id (bl_aid). Paths are sent without query strings and the server removes
 * identifiers; nothing goes to anyone else. Refusing deletes the visitor id.
 */
import { useSyncExternalStore } from "react";

const CONSENT = "bl_consent";
const VISITOR = "bl_aid";
export type Consent = "accepted" | "refused" | null;

function readCookie(name: string) {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
}
function writeCookie(name: string, value: string, days: number) {
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${Math.round(days * 86400)}; SameSite=Lax${secure}`;
}

export function consent(): Consent {
  const v = readCookie(CONSENT);
  return v === "v1:accepted" ? "accepted" : v === "v1:refused" ? "refused" : null;
}

// Who listens to the choice and to "Cookie settings" (to show the banner again).
const listeners = new Set<() => void>();
let bannerForced = false;
let snapshot = { consent: consent(), open: consent() === null };
function emit() {
  snapshot = { consent: consent(), open: bannerForced || consent() === null };
  for (const l of listeners) l();
}
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

/** The visitor's choice, and whether the banner should show. */
export function useConsent() {
  return useSyncExternalStore(subscribe, () => snapshot);
}

/** "Cookie settings": show the banner again to change the choice. */
export function openCookieSettings() {
  bannerForced = true;
  emit();
}

export function setConsent(choice: "accepted" | "refused") {
  writeCookie(CONSENT, `v1:${choice}`, choice === "accepted" ? 395 : 182);
  if (choice === "accepted") {
    if (!readCookie(VISITOR)) {
      const bytes = crypto.getRandomValues(new Uint8Array(16));
      writeCookie(VISITOR, Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join(""), 395);
    }
  } else {
    writeCookie(VISITOR, "", 0);
  }
  bannerForced = false;
  emit();
  // Only the count of choices per day: nothing identifies the visitor.
  void fetch("/api/analytics/consent", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ choice }), credentials: "same-origin", keepalive: true }).catch(() => undefined);
  if (choice === "accepted") track("page_view", undefined, currentScreen);
}

let currentScreen = window.location.pathname;
let sentReferrer = false;

/** Record a step (page view, sign-up, payment page…) if the visitor accepted. Never content. */
export function track(name: string, props?: Record<string, string | number | boolean>, path = window.location.pathname) {
  if (name === "page_view") currentScreen = path;
  if (consent() !== "accepted") return;
  const q = new URLSearchParams(window.location.search);
  const event = {
    name,
    path,
    referrer: sentReferrer ? null : document.referrer || null,
    utmSource: q.get("utm_source"),
    utmMedium: q.get("utm_medium"),
    utmCampaign: q.get("utm_campaign"),
    props,
  };
  sentReferrer = true;
  const body = JSON.stringify({ events: [event] });
  // sendBeacon survives leaving the page (e.g. on to Stripe).
  if (!navigator.sendBeacon?.("/api/analytics/events", new Blob([body], { type: "application/json" }))) {
    void fetch("/api/analytics/events", { method: "POST", headers: { "content-type": "application/json" }, body, credentials: "same-origin", keepalive: true }).catch(() => undefined);
  }
}
